/* eslint-disable max-len */
import { Form, Row } from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { ILeiData, ILeiMatka, ILeiSubory } from 'types/leiTypes';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import TableComponent from 'components/atoms/TableComponent';
import UploadComponent, { IFileList } from 'components/atoms/UploadComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import fileDownload from 'services/fileDownload';
import checkLeiApi from 'services/lei/checkLEI';
import prepareFile from 'services/lei/fileDownload';

import { EAnoNe } from 'enums/anoNe';
import { EPeriodType, ETypMatky } from 'enums/lei/leiDataEnums';

import anoNeSelect from 'data/codebooks/anoNeSelect';
import { leiTypFonduSelect } from 'data/codebooks/lei/leiTypFonduSelect';

import leiValidation from 'utils/validations/leiValidation';

interface IProfondy {
    nextStep: () => void;
    prevStep: () => void;
    setFormData: React.Dispatch<React.SetStateAction<ILeiData | undefined>>;
    vsechnyMatky?: ILeiMatka[];
}

const Profondy: React.FC<IProfondy> = ({ prevStep, nextStep, setFormData, vsechnyMatky }) => {
    const { lei, language } = useContext(Context);
    const [form] = Form.useForm();
    const [matkaData, setMatkaData] = useState<ILeiMatka | undefined>();
    const [submitMatkaData, setSubmitMatkaData] = useState<ILeiMatka | null>();
    const [matkaIndex, setMatkaIndex] = useState<number>(-1);
    const [saveOnly, setSaveOnly] = useState<boolean>();
    const [requiredFundManagedByErr, setRequiredFundManagedByErr] = useState<boolean>();
    const [deleteMatka, setDeleteMatka] = useState<number>(-1);
    const [leiCheckLoading, setLeiCheckLoading] = useState<boolean>(false);
    const [leiCheckError, setLeiCheckError] = useState<string>('');
    const jineMatky = vsechnyMatky?.filter(
        e =>
            e.typmatky === ETypMatky.IS_ULTIMATELY_CONSOLIDATED_BY ||
            e.typmatky === ETypMatky.IS_DIRECTLY_CONSOLIDATED_BY
    );
    const profondyMatky = vsechnyMatky?.filter(
        e =>
            e.typmatky !== ETypMatky.IS_ULTIMATELY_CONSOLIDATED_BY &&
            e.typmatky !== ETypMatky.IS_DIRECTLY_CONSOLIDATED_BY
    );
    const matkyError = profondyMatky?.map(
        e =>
            (e.typmatky === ETypMatky.IS_FUND_MANAGED_BY && e.selfmanaged
                ? true
                : e.informacekmatce?.relationshipperiod
                  ? e.informacekmatce.relationshipperiod.length > 0
                  : false) && (e.informacekmatce?.soubory ? e.informacekmatce.soubory.length > 0 : false)
    );

    const [fileList, setFileList] = useState<IFileList[] | undefined>(
        (matkaData && matkaData.informacekmatce?.soubory ? matkaData.informacekmatce.soubory : []).map((e, i) => {
            return {
                uid: String(i),
                name: e.orignazev ? e.orignazev : 'bez nazvu',
                fileName: e.tempnazev ? e.tempnazev : 'a',
                type: e.orignazev ? e.orignazev.split('.').slice(-1)[0] : '',
                size: 0,
                status: 'done'
            };
        })
    );
    const [prilozeneSoubory, setPrilozeneSoubory] = useState<ILeiSubory[]>(
        matkaData && matkaData.informacekmatce?.soubory ? matkaData.informacekmatce.soubory : []
    );
    const [uploading, setUploading] = useState<boolean>(false);

    const setForceRerender = useState<boolean>(false)[1];
    const rerender = () => {
        setForceRerender(val => !val);
    };

    const matkaDates = {
        accounting:
            matkaData &&
            matkaData.informacekmatce &&
            matkaData.informacekmatce.relationshipperiod?.filter(
                e => e.periodtype === EPeriodType.ACCOUNTING_PERIOD
            )[0],
        document:
            matkaData &&
            matkaData.informacekmatce &&
            matkaData.informacekmatce.relationshipperiod?.filter(
                e => e.periodtype === EPeriodType.DOCUMENT_FILING_PERIOD
            )[0]
    };

    useEffect(() => {
        if (matkaData) {
            matkaData.matkaslei && form.validateFields(['matkaslei']);
            setFileList(
                (matkaData && matkaData.informacekmatce?.soubory ? matkaData.informacekmatce.soubory : []).map(
                    (e, i) => {
                        return {
                            uid: String(i),
                            name: e.orignazev ? e.orignazev : 'bez nazvu',
                            fileName: e.tempnazev ? e.tempnazev : 'a',
                            type: e.orignazev ? e.orignazev.split('.').slice(-1)[0] : '',
                            size: 0,
                            status: 'done'
                        };
                    }
                )
            );
            setPrilozeneSoubory(
                matkaData && matkaData.informacekmatce?.soubory ? matkaData.informacekmatce.soubory : []
            );
        }
        rerender();

        form.setFieldsValue({
            documentstart: matkaDates && matkaDates.document ? dayjs(matkaDates.document.startdate) : undefined,
            documentend: matkaDates && matkaDates.document?.enddate ? dayjs(matkaDates.document.enddate) : undefined,
            typmatky: matkaData?.typmatky,
            selfmanaged:
                typeof matkaData?.selfmanaged === 'boolean'
                    ? matkaData?.selfmanaged
                        ? EAnoNe.ANO
                        : EAnoNe.NE
                    : undefined
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matkaData]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (fieldsValue: any) => {
        const formDataMatkaObj = {
            typmatky: fieldsValue.typmatky,
            matkaslei: fieldsValue.matkaslei,
            selfmanaged: fieldsValue.selfmanaged === EAnoNe.ANO,
            informacekmatce: {
                ...(fieldsValue.relationship && {
                    datumodkdyplatnost: matkaData?.informacekmatce?.datumodkdyplatnost || dayjs().format('YYYY-MM-DD')
                }),
                validationdocuments: fieldsValue.validationdocuments,
                soubory: prilozeneSoubory,
                ...(fieldsValue.validationreference && {
                    validationreference: fieldsValue.validationreference
                }),
                ...(fieldsValue.typmatky === ETypMatky.IS_FUND_MANAGED_BY && fieldsValue.selfmanaged === EAnoNe.ANO
                    ? {}
                    : {
                          relationshipperiod: [
                              ...(fieldsValue.documentstart
                                  ? [
                                        {
                                            periodtype: EPeriodType.DOCUMENT_FILING_PERIOD,
                                            ...(fieldsValue.documentstart && {
                                                startdate: fieldsValue.documentstart.format('YYYY-MM-DD')
                                            }),
                                            ...(fieldsValue.documentend && {
                                                enddate: fieldsValue.documentend.format('YYYY-MM-DD')
                                            })
                                        },
                                        ...(fieldsValue.relationship
                                            ? [
                                                  {
                                                      periodtype: EPeriodType.RELATIONSHIP_PERIOD,
                                                      startdate: fieldsValue.relationship.format('YYYY-MM-DD')
                                                  }
                                              ]
                                            : [])
                                    ]
                                  : [])
                          ]
                      })
            }
        };

        !saveOnly && setLeiCheckError('');
        setSubmitMatkaData(formDataMatkaObj);
        !saveOnly && form.resetFields();
    };

    useEffect(() => {
        saveOnly && onFinish(form.getFieldsValue());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveOnly]);

    useEffect(() => {
        if (submitMatkaData) {
            if (matkaIndex > -1 && profondyMatky) {
                profondyMatky[matkaIndex] = submitMatkaData;
            } else {
                setMatkaIndex(profondyMatky ? profondyMatky.length : 0);
                profondyMatky?.push(submitMatkaData);
            }
            setFormData(prevState => {
                return {
                    ...prevState,
                    matky: [...(jineMatky ? jineMatky : []), ...(profondyMatky ? profondyMatky : [])]
                };
            });
            (submitMatkaData.typmatky === ETypMatky.IS_FUND_MANAGED_BY ||
                (submitMatkaData.typmatky === ETypMatky.IS_SUBFUND_OF && submitMatkaData.selfmanaged)) &&
                setRequiredFundManagedByErr(false);
            !saveOnly && setMatkaIndex(-1);
            !saveOnly && setMatkaData(undefined);
            !saveOnly && setSubmitMatkaData(null);
            setSaveOnly(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitMatkaData]);

    useEffect(() => {
        if (deleteMatka > -1) {
            profondyMatky?.splice(deleteMatka, 1);
            setFormData(prevState => {
                return {
                    ...prevState,
                    matky: [...(jineMatky ? jineMatky : []), ...(profondyMatky ? profondyMatky : [])]
                };
            });
            setDeleteMatka(-1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deleteMatka]);

    const uploadProps = {
        showDownloadIcon: true,
        onChange: (data: UploadChangeParam) => {
            if (data.fileList.length) {
                if (data.file.status === 'uploading' && setUploading) setUploading(true);
                if (data.file.status !== 'uploading' && setUploading) setUploading(false);
                if (data.file.response) {
                    form.validateFields(['soubor']);
                    setPrilozeneSoubory(
                        data.fileList
                            .filter(f => f.status && f.status !== 'error')
                            .map(e => {
                                return {
                                    orignazev: e.name.normalize(),
                                    tempnazev: e.response ? e.response.tempnazev : e.fileName
                                };
                            })
                    );
                    setSaveOnly(true);
                }
                setFileList(
                    data.fileList
                        .filter(f => f.status && f.status !== 'error')
                        .map(e => {
                            return {
                                uid: e.uid,
                                name: e.name.normalize(),
                                fileName: e.fileName ? e.fileName : e.response ? e.response.tempnazev : '',
                                type: e.type!,
                                size: e.size!,
                                status: e.status ? e.status : 'error'
                            };
                        })
                );
            }
        },
        onRemove: (file: UploadFile) => {
            if (fileList?.length) {
                const oldState = [...fileList];
                const newState = oldState.filter(e => e.uid !== file.uid);
                setFileList(newState);
                const newSoubory = [...prilozeneSoubory].filter(e => e.tempnazev !== file.fileName);
                setPrilozeneSoubory(newSoubory);
                setSaveOnly(true);
            }
        },
        onDownload: (file: UploadFile) =>
            prepareFile(file.fileName ? file.fileName : '', lei?.idVarsymZadosti?.varsym || '').then(resp => {
                fileDownload(resp.redirect, file.name ? file.name : 'file');
            }),
        fileList: fileList,
        buttonDisabled: fileList ? fileList.length > 4 : false
    };

    const columns = [
        {
            title: <TranslateText>{language === 'en' ? 'VZTAH' : 'VZTAH_TYP'}</TranslateText>,
            dataIndex: 'typmatky',
            key: 'typmatky',
            render: (text: string) => (
                <p
                    style={{
                        margin: 0
                    }}
                >
                    <TranslateText>{`LEI_${text?.split('-').join('_')}`}</TranslateText>
                </p>
            )
        },
        {
            title: <TranslateText>LEI_VELKE</TranslateText>,
            dataIndex: 'matkaslei',
            key: 'matkaslei',
            render: (text: string) => (
                <p
                    style={{
                        margin: 0
                    }}
                >
                    {text}
                </p>
            )
        },
        {
            width: 100,
            render: (_text: string, row: ILeiMatka, index: number) => (
                <AnchorComponent
                    id={`fondyUpravitBtn${index}`}
                    key={index}
                    onClick={() => {
                        form.setFieldsValue({
                            ...row,
                            relationship: dayjs(
                                (row.informacekmatce?.relationshipperiod || []).length > 1
                                    ? row.informacekmatce?.relationshipperiod?.filter(
                                          o => o.periodtype === EPeriodType.RELATIONSHIP_PERIOD
                                      )[0].startdate
                                    : undefined
                            ),
                            validationdocuments: row.informacekmatce?.validationdocuments
                        });
                        setMatkaData(row);
                        setMatkaIndex(index);
                    }}
                >
                    <TranslateText>BUTTON_UPRAVIT</TranslateText>
                </AnchorComponent>
            )
        },
        {
            width: 100,
            render: (_text: ILeiMatka, _row: ILeiMatka, index: number) => (
                <AnchorComponent key={index} onClick={() => setDeleteMatka(index)} id={`fondyOdebratBtn${index}`}>
                    <TranslateText>ODEBRAT</TranslateText>
                </AnchorComponent>
            )
        }
    ];

    return (
        <>
            {!matkaData ? (
                <>
                    <Paragraph
                        text="LEI_FUND_TEXT1"
                        style={{
                            // marginTop: 12,
                            display: 'block',
                            marginBottom: 10
                        }}
                    />
                    <Paragraph
                        text="LEI_FUND_TEXT2"
                        style={{
                            // marginTop: 12,
                            display: 'block',
                            marginBottom: 10
                        }}
                    />
                    <Paragraph
                        text="LEI_FUND_TEXT3"
                        style={{
                            // marginTop: 12,
                            display: 'block',
                            marginBottom: 30
                        }}
                    />

                    <TableComponent
                        rowKey={(_, index) => index || 0}
                        locale={{
                            emptyText: <TranslateText>LEI_FOND_NENI</TranslateText>
                        }}
                        style={{
                            margin: '30px 0px'
                        }}
                        columns={columns}
                        rowClassName={(_, i) => (matkyError ? (matkyError[i] ? '' : 'tableRowRed') : '')}
                        dataSource={profondyMatky}
                        pagination={false}
                        footer={() => (
                            <AnchorComponent
                                id="fondyPridatOsobu"
                                style={{
                                    textAlign: 'center',
                                    display: 'block'
                                }}
                                onClick={() => {
                                    form.resetFields();
                                    setMatkaData({});
                                    setMatkaIndex(-1);
                                }}
                            >
                                <TranslateText>PRIDAT</TranslateText>
                            </AnchorComponent>
                        )}
                    />
                    {matkyError && matkyError?.filter(e => e === false).length > 0 && (
                        <div
                            className="extramessage-text"
                            style={{
                                display: 'inline'
                            }}
                        >
                            <TranslateText>LEI_PROFONDY_ERROR</TranslateText>
                        </div>
                    )}

                    <Row className="button-row">
                        <ButtonComponent onClick={() => prevStep()} buttontext="ZPET" type="default" id="fondZpetBtn" />
                        <ButtonComponent
                            id="fondSubmitBtn"
                            loading={uploading}
                            type="primary"
                            htmlType="button"
                            onClick={() =>
                                profondyMatky?.filter(
                                    e =>
                                        e.typmatky === ETypMatky.IS_FUND_MANAGED_BY ||
                                        (e.typmatky === ETypMatky.IS_SUBFUND_OF && e.selfmanaged)
                                ).length
                                    ? nextStep()
                                    : setRequiredFundManagedByErr(true)
                            }
                            disabled={
                                (matkyError && matkyError?.filter(e => e === false).length > 0) ||
                                (profondyMatky && profondyMatky.length < 1)
                            }
                            extramessage={requiredFundManagedByErr ? 'LEI_IS_FUND_MANAGED_BY_ERR' : ''}
                            buttontext="POKRACOVAT"
                        />
                    </Row>
                </>
            ) : (
                <FormWrapper outForm={form} onFinish={onFinish}>
                    <SelectComponent
                        isFormItem={true}
                        name="typmatky"
                        defaultValue={matkaData?.stav}
                        labelParagraph="VZTAH_TYP"
                        data={leiTypFonduSelect}
                        onChange={rerender}
                    />
                    {(form.getFieldValue('typmatky') === ETypMatky.IS_FUND_MANAGED_BY ||
                        form.getFieldValue('typmatky') === ETypMatky.IS_SUBFUND_OF) && (
                        <SelectComponent
                            name="selfmanaged"
                            isFormItem={true}
                            labelParagraph="FUND_SELFMANAGED"
                            defaultValue={
                                typeof matkaData?.selfmanaged === 'boolean'
                                    ? matkaData?.selfmanaged
                                        ? EAnoNe.ANO
                                        : EAnoNe.NE
                                    : undefined
                            }
                            onChange={rerender}
                            data={anoNeSelect}
                        />
                    )}
                    {(form.getFieldValue('selfmanaged') === 'ne' ||
                        (form.getFieldValue('typmatky') &&
                            form.getFieldValue('typmatky') !== ETypMatky.IS_FUND_MANAGED_BY)) && (
                        <>
                            <LoadingWrapper loading={leiCheckLoading} loadingText="NACITANI_DAT">
                                <InputComponent
                                    label="LEI_VELKE"
                                    name="matkaslei"
                                    initialValue={matkaData?.matkaslei}
                                    rules={[
                                        ...leiValidation(async (value: string) => {
                                            if (
                                                value !== lei?.detailData?.spolecnost?.legalentityidentifier ||
                                                (value === lei?.detailData?.spolecnost?.legalentityidentifier &&
                                                    form.getFieldValue('typmatky') === ETypMatky.IS_FUND_MANAGED_BY)
                                            ) {
                                                setLeiCheckLoading(true);
                                                const resp = value ? await checkLeiApi(value).catch(() => null) : null;
                                                setLeiCheckLoading(false);
                                                form.setFieldsValue({
                                                    leinazev: resp ? resp.spolecnost : undefined
                                                });
                                                rerender();
                                                if (
                                                    resp?.stavlei === 'RETIRED' ||
                                                    resp?.stavlei === 'ANNULLED' ||
                                                    resp?.stavlei === 'DUPLICATE'
                                                ) {
                                                    return setLeiCheckError('CHYBNE_LEI');
                                                }
                                                if (!resp && value) {
                                                    return setLeiCheckError('LEI_CHECK_ERROR');
                                                }
                                                return setLeiCheckError('');
                                            }
                                        }),
                                        () => ({
                                            validator(_, value: string) {
                                                if (
                                                    value === lei?.detailData?.spolecnost?.legalentityidentifier &&
                                                    form.getFieldValue('typmatky') !== ETypMatky.IS_FUND_MANAGED_BY
                                                )
                                                    return Promise.reject(
                                                        <TranslateText>LEI_FUND_STEJNE_ERR</TranslateText>
                                                    );
                                                if (!value || !leiCheckError) return Promise.resolve();
                                                return Promise.reject();
                                            }
                                        })
                                    ]}
                                    maxLength={20}
                                    onChange={() => setLeiCheckError('')}
                                    validateTrigger={['onBlur', 'onSubmit']}
                                    validateStatus={leiCheckError ? 'error' : undefined}
                                    extramessage={leiCheckError}
                                />
                                {form.getFieldValue('leinazev') && (
                                    <InputComponent
                                        label="NAZEV"
                                        name="leinazev"
                                        placeholder=""
                                        disabled={true}
                                        initialValue={''}
                                        maxLength={80}
                                        notRequired={true}
                                        rules={[]}
                                    />
                                )}
                            </LoadingWrapper>
                            <DatePickerComponent label="DATUM_ZACATEK_VZTAHU" name="relationship" />
                        </>
                    )}
                    <Form.Item
                        name="soubor"
                        label={<Paragraph text={language === 'en' ? 'LEI_FINAL_UPLOAD' : 'SOUBOR'} strong={true} />}
                        rules={[
                            {
                                required: true,
                                validator() {
                                    try {
                                        if (!fileList || fileList.length < 1)
                                            return Promise.reject(
                                                <TranslateText>NEVALIDNI_POVINNA_POLOZKA_UPLOAD</TranslateText>
                                            );
                                        return Promise.resolve();
                                    } catch {
                                        return Promise.reject();
                                    }
                                }
                            }
                        ]}
                    >
                        <div
                            style={{
                                marginBottom: 22
                            }}
                        >
                            {language !== 'en' && (
                                <p>
                                    <TranslateText>LEI_FOND_UPLOAD_TEXT</TranslateText>
                                </p>
                            )}
                            <UploadComponent {...uploadProps} accept={'.pdf, .rtf, .docx, .doc'} id="fondyUploadBtn" />
                        </div>
                    </Form.Item>
                    {(fileList || []).length > 0 &&
                        (form.getFieldValue('typmatky') !== ETypMatky.IS_FUND_MANAGED_BY ||
                            form.getFieldValue('selfmanaged') !== 'ano') && (
                            <>
                                <InputComponent
                                    label={language === 'en' ? 'LEI_DETAIL_REGULATORY_FUND' : 'LEI_DETAIL_REGULATORY'}
                                    name="validationreference"
                                    initialValue={matkaData && matkaData.informacekmatce?.validationreference}
                                    onChange={() => rerender()}
                                    maxLength={100}
                                    notRequired={true}
                                />

                                <DatePickerComponent
                                    disabledDate={current =>
                                        form.getFieldValue('documentend') &&
                                        current > dayjs(form.getFieldValue('documentend')).subtract(1, 'days')
                                    }
                                    label="LEI_DETAIL_PLATNOSTDOKUMENTU_OD"
                                    name="documentstart"
                                />
                                <DatePickerComponent
                                    disabledDate={current =>
                                        form.getFieldValue('documentstart') &&
                                        current < dayjs(form.getFieldValue('documentstart')).add(1, 'days')
                                    }
                                    label="LEI_DETAIL_PLATNOSTDOKUMENTU_DO"
                                    name="documentend"
                                    notRequired={true}
                                />
                            </>
                        )}
                    <Row className="button-row">
                        <ButtonComponent
                            id="fondZpetBtn"
                            onClick={() => {
                                form.resetFields();
                                setSubmitMatkaData(null);
                                setMatkaData(undefined);
                                setMatkaIndex(-1);
                            }}
                            buttontext="ZPET"
                            type="default"
                        />
                        <ButtonComponent
                            loading={uploading}
                            id="fondSubmitBtn"
                            type="primary"
                            onClick={() => form.submit()}
                            buttontext="POKRACOVAT"
                        />
                    </Row>
                </FormWrapper>
            )}
        </>
    );
};

export default Profondy;
